// 012B — Imagenología Informe (Anexo 1 MSP, SNS-MSP/HCU-form.012B/2021). Fase 7, Tanda 3: datos del servicio
// (profesional que realiza y solicitante, fecha de realización, servicio, especialidad), estudio realizado con las
// mismas casillas de 012A, hallazgos, conclusiones y sugerencias. El papel no trae diagnóstico CIE ni "eco
// obstétrica" (estaban inventados y se retiraron de la UI). Campos y decisiones en `bloquesImagenologiaDatos.ts`.
import { useMedplum } from '@medplum/react-hooks';
import type { ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { AreaDictado } from './AreaDictado';
import { CATEGORIA_IMAGENOLOGIA } from './categoriasDiagnosticas';
import { Bloque, CamposSimples } from './camposSimples';
import { SelectorRecurso } from './SelectorRecurso';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_CONCLUSIONES_012B,
  CAMPOS_ESTUDIO_IMAGEN,
  CAMPOS_HALLAZGOS_012B,
  CAMPOS_SERVICIO_012B,
  conclusionInforme012B,
  estadoInicial012B,
  estudiosMarcados,
  LEYENDA_012B,
  paresDe012B,
  type Estado012B,
} from './bloquesImagenologiaDatos';

const CAMPOS_ESTUDIO_SIN_DESCRIPCION = CAMPOS_ESTUDIO_IMAGEN.filter((c) => c.clave !== 'descripcion');
const CAMPO_DESCRIPCION = CAMPOS_ESTUDIO_IMAGEN.find((c) => c.clave === 'descripcion')!;

export function Formulario012B({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [solicitud, setSolicitud] = useState<ServiceRequest>();
  const [estado, setEstado] = useState<Estado012B>(() => estadoInicial012B());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '012B', t('formularios.f012b.titulo'), { servicio: estado.servicio.servicio });

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahora = new Date().toISOString();
      const fecha = estado.servicio.fechaRealizacion ? new Date(`${estado.servicio.fechaRealizacion}T00:00:00`).toISOString() : ahora;
      const estudios = estudiosMarcados(estado.estudio).join(', ');
      await medplum.createResource({
        resourceType: 'DiagnosticReport',
        status: 'final',
        category: [CATEGORIA_IMAGENOLOGIA],
        code: solicitud?.code ?? { text: estudios || CATEGORIA_IMAGENOLOGIA.text },
        subject,
        encounter,
        basedOn: solicitud?.id ? [{ reference: `ServiceRequest/${solicitud.id}` }] : undefined,
        effectiveDateTime: fecha,
        issued: ahora,
        conclusion: conclusionInforme012B(estado),
      });
      const pares = [...(solicitud ? [{ pregunta: t('formularios.f012b.solicitudOrigen'), respuesta: solicitud.code?.text ?? solicitud.id ?? '' }] : []), ...paresDe012B(estado)];
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f012b.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <SelectorRecurso<'ServiceRequest'>
        resourceType="ServiceRequest"
        subject={subject}
        label={t('formularios.f012b.solicitudOrigen')}
        etiqueta={(r) => r.code?.text ?? r.id ?? ''}
        value={solicitud}
        onChange={setSolicitud}
      />

      <Bloque titulo={t('formularios.f012b.servicioTitulo')}>
        <CamposSimples prefijoId="f012b-srv" campos={CAMPOS_SERVICIO_012B} valores={estado.servicio} columnas={3} onChange={(v) => setEstado({ ...estado, servicio: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f012b.estudioTitulo')}>
        <CamposSimples prefijoId="f012b-est" campos={CAMPOS_ESTUDIO_SIN_DESCRIPCION} valores={estado.estudio} columnas={4} onChange={(v) => setEstado({ ...estado, estudio: v })} />
        <AreaDictado id="f012b-est-descripcion" etiqueta={CAMPO_DESCRIPCION.etiqueta} valor={estado.estudio.descripcion ?? ''} filas={3} onChange={(v) => setEstado({ ...estado, estudio: { ...estado.estudio, descripcion: v } })} />
      </Bloque>

      <Bloque titulo={t('formularios.f012b.hallazgosTitulo')}>
        <AreaDictado id="f012b-hallazgos" etiqueta={CAMPOS_HALLAZGOS_012B[0]!.etiqueta} valor={estado.hallazgos.hallazgos ?? ''} filas={10} onChange={(v) => setEstado({ ...estado, hallazgos: { hallazgos: v } })} />
      </Bloque>

      <Bloque titulo={t('formularios.f012b.conclusionesTitulo')}>
        <AreaDictado id="f012b-conclusiones" etiqueta={CAMPOS_CONCLUSIONES_012B[0]!.etiqueta} valor={estado.conclusiones.conclusiones ?? ''} filas={4} onChange={(v) => setEstado({ ...estado, conclusiones: { conclusiones: v } })} />
        <p className="text-xs text-hce-muted">{LEYENDA_012B}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
